"use client";

import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";

/**
 * Dropdown shown below the search input for recent searches or company suggestions.
 * mode = "recent" | "company"
 */
export default function SearchDropdown({
  mode,
  items,
  onSelect,
  onRemove,
  onClearAll,
}) {
  const theme = useTheme();

  if (!items || items.length === 0) return null;

  const isRecent = mode === "recent";

  return (
    <Box
      sx={{
        position: { xs: "relative", sm: "absolute" },
        top: { sm: "100%" },
        left: { sm: 0 },
        right: { sm: 0 },
        width: { xs: "100%", sm: "auto" },
        mt: { xs: 1, sm: 0.5 },
        zIndex: 1300,
        backgroundColor: isRecent
          ? alpha(theme.palette.common.white, 0.08)
          : theme.palette.background.paper,
        backgroundImage: "none",
        backdropFilter: isRecent ? "blur(28px) saturate(180%)" : "none",
        WebkitBackdropFilter: isRecent ? "blur(28px) saturate(180%)" : "none",
        border: isRecent
          ? `1px solid ${alpha(theme.palette.common.white, 0.22)}`
          : `1px solid ${theme.palette.divider}`,
        borderTop: isRecent
          ? `1px solid ${alpha(theme.palette.common.white, 0.28)}`
          : "none",
        borderBottom: isRecent
          ? `1px solid ${alpha(theme.palette.common.white, 0.14)}`
          : "none",
        borderLeft: isRecent ? "none" : "unset",
        borderRight: isRecent ? "none" : "unset",
        borderRadius: isRecent ? 0 : "12px",
        boxShadow: isRecent ? "none" : theme.shadows[4],
        overflow: "hidden",
      }}
    >
      {isRecent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Recent Searches
          </Typography>
          <Typography
            variant="caption"
            onClick={onClearAll}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <List dense disablePadding>
        {items.map((item, i) => (
          <ListItem
            key={i}
            disablePadding
            secondaryAction={
              isRecent && onRemove ? (
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item);
                  }}
                  sx={{ color: theme.palette.text.disabled }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null
            }
          >
            <ListItemButton
              onClick={() => onSelect(item)}
              sx={{
                px: 2,
                py: 1,
                backgroundColor: isRecent ? "transparent" : "transparent",
                "&:hover": {
                  backgroundColor: isRecent
                    ? alpha(theme.palette.common.white, 0.18)
                    : theme.palette.action.hover,
                },
              }}
            >
              {isRecent ? (
                <HistoryIcon
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.disabled,
                    mr: 1.5,
                  }}
                />
              ) : (
                <BusinessIcon
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.disabled,
                    mr: 1.5,
                  }}
                />
              )}
              <ListItemText
                primary={item}
                slotProps={{
                  primary: {
                    fontSize: "0.9rem",
                    color: theme.palette.text.primary,
                    noWrap: true,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
